import { Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild, computed, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OverlayContainer } from '@angular/cdk/overlay';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LoaderMessage, LoaderMessageService } from './common/auth/loader-message.service';

const MESSAGE_KEYS: Record<NonNullable<LoaderMessage>, string> = {
  analyzing: 'Analyzing_document_tr',
  uploading: 'Uploading_document_tr',
  translating: 'Translating_document_tr',
};

/**
 * The single global loading overlay, and the owner of its DOM.
 *
 * It reparents itself into the topmost open dialog pane so the spinner centers over that surface
 * (a "right sidebar" is just a dialog with a slide-in panelClass). No geometry measurement:
 * position:absolute inside the pane does the centering for free.
 */
@Component({
  selector: 'app-loader-spinner',
  imports: [MatProgressSpinnerModule, TranslatePipe],
  template: `
    <div #loader [class.hide]="hideLoader" class="loader-spinner">
      <mat-progress-spinner mode="indeterminate" diameter="60"></mat-progress-spinner>
      @if (messageKey(); as key) {
        <div class="loader-spinner__message">{{ key | translate }}</div>
      }
    </div>
  `,
  styleUrl: 'loader-spinner.component.scss',
})
export class LoaderSpinnerComponent implements OnInit, OnDestroy {
  private readonly overlay = inject(OverlayContainer);
  private readonly loader = inject(LoadingBarService);
  private readonly dialog = inject(MatDialog);
  private readonly renderer = inject(Renderer2);
  private readonly message = inject(LoaderMessageService).message;

  hideLoader = false;
  readonly messageKey = computed(() => {
    const message = this.message();
    return message ? MESSAGE_KEYS[message] : null;
  });

  @ViewChild('loader', { static: true }) private readonly loaderSpinner!: ElementRef<HTMLElement>;
  private loaderHome?: { parent: Node; next: Node | null };
  private loaderHostPane: HTMLElement | null = null;
  private readonly dialogSubs = new Subscription();

  ngOnInit(): void {
    const loaderEl = this.loaderSpinner.nativeElement;
    this.loaderHome = { parent: loaderEl.parentNode!, next: loaderEl.nextSibling };
    this.loader.value$.subscribe((v) => {
      this.hideLoader = !v;
      // Scope as soon as the loader becomes visible: the dialog pane is in the DOM right after
      // dialog.open(), well before afterOpened fires, so this avoids a brief full-screen flash.
      if (v) this.scopeLoaderToTopmostDialog();
    });
    this.dialogSubs.add(this.dialog.afterOpened.subscribe((ref) => this.onDialogOpened(ref)));
  }

  ngOnDestroy(): void {
    this.dialogSubs.unsubscribe();
    this.restoreLoaderHome();
  }

  private onDialogOpened(ref: MatDialogRef<unknown>) {
    this.scopeLoaderToTopmostDialog();
    // Move the loader back home BEFORE this pane is torn down, otherwise it would be removed
    // from the DOM together with the closing pane; afterClosed re-scopes to whatever remains.
    this.dialogSubs.add(ref.beforeClosed().subscribe(() => this.restoreLoaderHome()));
    this.dialogSubs.add(ref.afterClosed().subscribe(() => this.scopeLoaderToTopmostDialog()));
  }

  private scopeLoaderToTopmostDialog() {
    const panes = this.overlay.getContainerElement().querySelectorAll<HTMLElement>('.cdk-overlay-pane');
    let topmost: HTMLElement | null = null;
    panes.forEach((pane) => {
      if (pane.querySelector('.mat-mdc-dialog-container')) topmost = pane;
    });

    if (!topmost) {
      this.restoreLoaderHome();
      return;
    }
    if (topmost === this.loaderHostPane) return;

    if (this.loaderHostPane) this.renderer.removeClass(this.loaderHostPane, 'loader-host');
    const loaderEl = this.loaderSpinner.nativeElement;
    this.renderer.appendChild(topmost, loaderEl);
    loaderEl.classList.add('loader-spinner--scoped');
    // Dialogs opened with a position (e.g. the link sidebar, position:{right,top}) get a
    // position:static pane from CDK's GlobalPositionStrategy, which re-applies on every
    // updatePosition(). A 'loader-host' class + a !important stylesheet rule makes the pane a
    // containing block that survives those re-applies, so inset:0 fills the dialog/sidebar.
    this.renderer.addClass(topmost, 'loader-host');
    this.loaderHostPane = topmost;
  }

  private restoreLoaderHome() {
    if (!this.loaderHostPane || !this.loaderHome) return;
    this.renderer.removeClass(this.loaderHostPane, 'loader-host');
    const loaderEl = this.loaderSpinner.nativeElement;
    loaderEl.classList.remove('loader-spinner--scoped');
    this.renderer.insertBefore(this.loaderHome.parent, loaderEl, this.loaderHome.next);
    this.loaderHostPane = null;
  }
}
